// src/pages/RepayLoan.js
import React, { useState, memo } from 'react';
import './repay-loan.css';

import idfcLogo from '../assets/images/idfc_logo_for_ac.jpg';
import idfcQr from '../assets/images/idfcqrcode.png';
import {
  repayOldUserHistoryUrl,
  repayPaymentSuccessUrl,
  repayPayuHashUrl,
} from '../config/repayPayment';

const banks = [
  {
    name: 'IDFC FIRST Bank',
    logo: idfcLogo,
    qr: idfcQr,
    upi: 'marathonpvtltd@idfcbank',
    accountName: 'Marathon Finlease Private Limited',
    accountNumber: '62612199511',
    ifsc: 'IDFB0020151',
    branch: 'G-61 & G-62 Noida Sector-18',
    accountType: 'Current Account',
  },
];

const formatCurrency = (value) => {
  const amount = Number(String(value || '').replace(/,/g, ''));
  if (Number.isNaN(amount)) return '—';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(amount);
};

const formatRepayDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const getStatusClass = (status) => {
  const key = String(status || '').trim().toUpperCase();
  if (key === 'DISBURSED') return 'is-disbursed';
  if (key === 'CLOSED' || key === 'CLOSE') return 'is-closed';
  if (key === 'REJECT' || key === 'REJECTED') return 'is-reject';
  return 'is-default';
};

const getTillDateAmount = (customer) => {
  if (customer.pan === 'GFYPS2700N') return 83700;
  return customer.repayAmount;
};

const getOutstandingAmount = (customer) =>
  customer.outstandingAmount ?? customer.repayAmount ?? customer.tillDateRepayAmount;

const CustomerResultTable = memo(({ customer, payUing, onPayU }) => (
  <div className="repay-result">
    <div className="repay-result__head">
      <div>
        <p className="repay-result__eyebrow">Loan details found</p>
        <h2 className="repay-result__title">{customer.name}</h2>
        <p className="repay-result__meta">
          Loan No. <span>{customer.loan_no || '—'}</span>
        </p>
      </div>
      <span className={`repay-status ${getStatusClass(customer.status)}`}>
        {customer.status || '—'}
      </span>
    </div>

    <div className="repay-result__cards">
      <article className="repay-metric">
        <span>Repay Amount</span>
        <strong>{formatCurrency(customer.tillDateRepayAmount)}</strong>
      </article>
      <article className="repay-metric repay-metric--highlight">
        <span>Till Date Repay Amount</span>
        <strong>{formatCurrency(getTillDateAmount(customer))}</strong>
      </article>
      <article className="repay-metric">
        <span>Pending Amount</span>
        <strong>{formatCurrency(getOutstandingAmount(customer))}</strong>
      </article>
    </div>

    <div className="table-responsive repay-table-wrap">
      <table className="repay-table">
        <thead>
          <tr>
            <th>Customer Name</th>
            <th>Mobile</th>
            <th>PAN No</th>
            <th>Repay Amount</th>
            <th>Till Date Repay Amount</th>
            <th>Part Payment</th>
            <th>Pending Amount</th>
            <th>Status</th>
            <th>Repay Date</th>
            <th>PayU</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td data-label="Customer Name" className="repay-table__name">{customer.name}</td>
            <td data-label="Mobile">{customer.mobile}</td>
            <td data-label="PAN No" className="repay-table__pan">{customer.pan}</td>
            <td data-label="Repay Amount" className="repay-table__amount">{formatCurrency(customer.tillDateRepayAmount)}</td>
            <td data-label="Till Date Repay Amount" className="repay-table__amount repay-table__amount--due">{formatCurrency(getTillDateAmount(customer))}</td>
            <td data-label="Part Payment" className="repay-table__amount">{formatCurrency(customer.partPaymentReceived)}</td>
            <td data-label="Pending Amount" className="repay-table__amount repay-table__amount--due">{formatCurrency(getOutstandingAmount(customer))}</td>
            <td data-label="Status">
              <span className={`repay-status ${getStatusClass(customer.status)}`}>
                {customer.status}
              </span>
            </td>
            <td data-label="Repay Date">{formatRepayDate(customer.repayDate)}</td>
            <td data-label="PayU" className="repay-table__action">
              <button
                type="button"
                className="repay-pay-btn repay-pay-btn--payu"
                onClick={onPayU}
                disabled={payUing}
              >
                {payUing ? 'Processing...' : `PayU ${formatCurrency(getOutstandingAmount(customer))}`}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
));

const BankCard = memo(({ bank }) => (
  <div className="row align-items-center repay-card mb-5 p-4 shadow rounded-4 bg-white">
    <div className="col-md-7 mb-4 mb-md-0">
      <div className="text-center mb-3">
        <img src={bank.logo} alt={`${bank.name} logo`} className="bank-logo mb-2" style={{ height: '60px' }} />
      </div>
      <h4 className="fw-bold text-primary">Bank Details</h4>
      <div className="bank-details ps-md-3">
        <p><strong>Bank Name:</strong> {bank.name}</p>
        <p><strong>Company Name:</strong> {bank.accountName}</p>
        <p><strong>Account No.:</strong> {bank.accountNumber}</p>
        <p><strong>IFSC Code:</strong> {bank.ifsc}</p>
        <p><strong>Branch Name:</strong> {bank.branch}</p>
        <p><strong>Account Type:</strong> {bank.accountType}</p>
      </div>
    </div>
    <div className="col-md-5 text-center">
      <h4 className="fw-bold text-primary">QR Code / UPI</h4>
      <p className="fw-semibold">SCAN & PAY</p>
      {bank.qr && <img src={bank.qr} alt={`${bank.name} QR Code`} className="qr-image mb-3" style={{ width: '260px', borderRadius: '10px' }} />}
      {bank.upi && <p className="mb-0"><strong>UPI ID:</strong> {bank.upi}</p>}
    </div>
  </div>
));

const RepayLoan = () => {
  const [mobile, setMobile] = useState('');
  const [customer, setCustomer] = useState(null);
  const [error, setError] = useState('');
  const [payUing, setPayUing] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    setError('');
    setCustomer(null);

    try {
      const response = await fetch(repayOldUserHistoryUrl(mobile));
      const data = await response.json();

      if (data?.listTask?.length > 0) {
        const user = data.listTask[0];
        setCustomer({
          name: user.first_name,
          mobile: user.mobile,
          lead_id: user.lead_id,
          loan_no: user.loan_no,
          pan: user.pancard,
          repayAmount: user.total_repayable,
          tillDateRepayAmount: user.repayment_amount,
          partPaymentReceived: user.part_payment_received ?? 0,
          outstandingAmount: user.outstanding_amount || user.total_repayable || user.repayment_amount,
          repayDate: user.repayment_date,
          status: user.status,
          email: user.email || 'user@example.com',
          id: user.id || 'CUS',
        });
      } else {
        setError('Customer not found');
      }
    } catch (err) {
      console.error(err);
      setError('Something went wrong. Please try again later.');
    }
  };

  const payUNow = async () => {
    if (payUing || !customer) return;

    setPayUing(true);

    try {
      const params = new URLSearchParams();
      params.append('amount', getOutstandingAmount(customer) ?? 0);
      params.append('lead_id', customer.lead_id);
      params.append('name', customer.name);
      params.append('email', customer.email);
      params.append('mobile', customer.mobile);
      params.append('loan_no', customer.loan_no);
      // Ask backend to return user to Fundswalle SPA thank-you (if supported in hash)
      const successUrl = repayPaymentSuccessUrl();
      params.append('surl', successUrl);
      params.append('furl', successUrl);

      const res = await fetch(repayPayuHashUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      if (!res.ok) {
        throw new Error('Server error');
      }

      const resp = await res.json();
      if (!resp.status) {
        alert(resp.message || 'PayU init failed');
        return;
      }

      const form = document.createElement('form');
      form.method = 'POST';
      form.action = resp.payu_url;

      // Do NOT rewrite surl/furl here — they are part of PayU hash from backend
      Object.entries(resp.params || {}).forEach(([key, value]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = key;
        input.value = value;
        form.appendChild(input);
      });

      document.body.appendChild(form);
      form.submit();
    } catch (err) {
      console.error('PAYU ERROR:', err);
      alert('Payment failed. Please try again.');
    } finally {
      setPayUing(false);
    }
  };

  return (
    <div className="repay-loan-page">
      <section className="hero-section text-white text-center py-3">
        <div className="container">
          <h1 className="fw-bold">Repay Your Loan</h1>
          <p className="lead mt-3 repay-subtitle">
            Choose your preferred bank and make hassle-free repayments.
          </p>
        </div>
      </section>

      <section className="container mt-5">
        <form onSubmit={handleSearch} className="d-flex flex-column flex-md-row align-items-md-center gap-3 mb-4">
          <input
            type="text"
            className="form-control"
            placeholder="Enter Mobile Number / PAN Number"
            value={mobile}
            onChange={(e) => setMobile(e.target.value)}
            required
          />
          <button type="submit" className="btn custom-submit-btn">Submit</button>
        </form>

        {error && <div className="alert alert-danger">{error}</div>}

        {customer && (
          <CustomerResultTable
            customer={customer}
            payUing={payUing}
            onPayU={payUNow}
          />
        )}
      </section>

      <section className="mt-5 bg-light">
        <div className="container">
          {banks.map((bank, index) => (
            <BankCard key={index} bank={bank} />
          ))}
        </div>
      </section>
    </div>
  );
};

export default RepayLoan;
